// Owner: design-system (plan §9.1, owner feedback F3). The brand accent as the UI kit's accent tokens:
// one style sheet for the app frame and the public surfaces that look like the app (sign-in, forms),
// and inline variables for miniatures (the Branding preview). No data access here, so public pages
// can use it without the app's queries.
import type { CSSProperties } from 'react';
import { type AccentTokens, brandAccentTokens } from '@dataroom/core/doc/theme';

const STYLE_ID = 'dr-brand-accent';

const block = (tokens: AccentTokens) =>
  Object.entries(tokens)
    .map(([k, v]) => `${k}:${v}`)
    .join(';');

/**
 * The style sheet that repaints the UI kit's accent tokens in a brand accent, for the light and the
 * dark theme (and `data-theme` subtrees such as theme previews).
 */
export function brandAccentCss(hex: string): string {
  const { light, dark } = brandAccentTokens(hex);
  // Doubled selectors outrank the kit's own token rules whatever order the sheets load in.
  return `:root:root,[data-theme='light'][data-theme]{${block(light)}}[data-theme='dark'][data-theme]{${block(dark)}}`;
}

/** The accent tokens of one scheme as inline CSS variables (public pages that are always light). */
export function brandAccentStyle(
  hex: string | null | undefined,
  scheme: 'light' | 'dark' = 'light',
): CSSProperties {
  if (!hex) return {};
  return brandAccentTokens(hex)[scheme];
}

/** Put the accent sheet in place (or remove it for the product accent). */
export function applyBrandAccent(hex: string | null): void {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!hex) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  const css = brandAccentCss(hex);
  if (el.textContent !== css) el.textContent = css;
}
